'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { effectStrength, type DrawnEffect } from '@/lib/public/effects/strength';
import { cachedTopoContours } from '@/lib/public/effects/topo';
import { chalkDrawing, chalkGrain, halftoneDots, sprintStreaks } from '@/lib/public/effects/shapes';
import { mix } from '@/lib/public/theme-colours';

/**
 * The six drawn background effects: Lane lines, Topo, Sprint, Chalk, Outline
 * type and Halftone. Each takes the organiser's accent, text colour and
 * heading font, draws in one layer whose opacity is solved against the page
 * (see lib/public/effects/strength.ts), and fades away from the text side.
 *
 * Phones pay little: SVG and CSS are static, the canvases are drawn once per
 * size (never per frame), and Sprint is the only thing that moves, by
 * transform alone. Nothing uses blur. Reduced motion stops Sprint where its
 * streaks rest, which is laid out to look finished.
 *
 * `mini` draws the same effect for a picker tile: finer detail and a little
 * stronger, so a 100 px tile still shows what the page will get.
 */

export interface DrawnEffectProps {
  effect: DrawnEffect;
  page: string;
  accent: string;
  text: string;
  /** The event name (see effectSeed): Topo's terrain, the streaks and the chalk all grow from it. */
  seed: string;
  /** Outline type's word. */
  word: string;
  /** CSS font-family for Outline type and the lane numbers. */
  headingFont: string;
  headingWeight?: string | number;
  mini?: boolean;
}

/** Each effect fades from where it lives (top right) towards the text. */
const MASKS: Record<DrawnEffect, { page: string[]; mini: string[] }> = {
  lanes: {
    page: ['linear-gradient(to right, transparent 6%, #000 58%)', 'linear-gradient(to bottom, #000 66%, transparent 92%)'],
    mini: ['linear-gradient(to right, transparent 0%, #000 55%)'],
  },
  sprint: {
    page: ['linear-gradient(to right, rgba(0,0,0,.12), #000 62%)', 'linear-gradient(to bottom, #000 45%, transparent 100%)'],
    mini: ['linear-gradient(to right, rgba(0,0,0,.2), #000 60%)'],
  },
  outline: {
    page: ['linear-gradient(to right, transparent 14%, #000 66%)', 'linear-gradient(to bottom, #000 58%, transparent 100%)'],
    mini: ['linear-gradient(to right, transparent 0%, #000 50%)'],
  },
  topo: {
    page: ['radial-gradient(ellipse 82% 100% at 96% 0%, #000 0%, rgba(0,0,0,.7) 40%, transparent 100%)', 'linear-gradient(to bottom, #000 40%, transparent 96%)'],
    mini: ['radial-gradient(ellipse 130% 140% at 95% 0%, #000 0%, rgba(0,0,0,.85) 45%, transparent 100%)'],
  },
  chalk: {
    page: ['radial-gradient(ellipse 85% 100% at 96% 0%, #000 0%, rgba(0,0,0,.75) 42%, transparent 100%)', 'linear-gradient(to bottom, #000 40%, transparent 96%)'],
    mini: ['radial-gradient(ellipse 130% 140% at 95% 0%, #000 0%, rgba(0,0,0,.85) 45%, transparent 100%)'],
  },
  halftone: {
    page: ['linear-gradient(to bottom, #000 50%, transparent 100%)'],
    mini: [],
  },
};

function maskStyle(layers: string[]): CSSProperties {
  if (!layers.length) return {};
  const image = layers.join(', ');
  const many = layers.length > 1;
  return {
    maskImage: image,
    WebkitMaskImage: image,
    ...(many ? { maskComposite: 'intersect', WebkitMaskComposite: 'source-in' } : {}),
  } as CSSProperties;
}

const fill: CSSProperties = { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };

/** The streak animation and the reduced-motion stop. Inline, so it also reaches the Branding preview's iframe. */
const SPRINT_CSS = `
@keyframes v2-fx-sprint { from { transform: translate3d(-260%, 0, 0); } to { transform: translate3d(420%, 0, 0); } }
.v2-fx-streak { animation: v2-fx-sprint var(--d) linear infinite; animation-delay: var(--dl); will-change: transform; }
@media (prefers-reduced-motion: reduce) { .v2-fx-streak { animation: none !important; will-change: auto; } }
`;

export function DrawnEffectLayer(props: DrawnEffectProps) {
  const { effect, page, accent, text, mini } = props;
  const { ink, opacity } = effectStrength(effect, page, accent, text, mini ? 1.45 : 1);
  if (opacity === 0) return null;
  return (
    <div aria-hidden="true" data-effect={effect} style={{ ...fill, overflow: 'hidden', opacity, ...maskStyle(MASKS[effect][mini ? 'mini' : 'page']) }}>
      {effect === 'lanes' && <Lanes ink={ink} font={props.headingFont} weight={props.headingWeight} mini={mini} />}
      {effect === 'topo' && <Topo ink={ink} seed={props.seed} mini={mini} />}
      {effect === 'sprint' && <Sprint ink={ink} tip={mix(ink, text, 0.45)} seed={props.seed} count={mini ? 12 : 22} />}
      {effect === 'chalk' && <Chalk ink={ink} seed={props.seed} mini={mini} />}
      {effect === 'outline' && <Outline ink={ink} word={props.word} font={props.headingFont} weight={props.headingWeight} mini={mini} />}
      {effect === 'halftone' && <Halftone ink={ink} mini={mini} />}
    </div>
  );
}

// ── Lane lines ──────────────────────────────────────────────────────────────

/** The bend of a 400 m track: eight lanes, the start line and lane numbers in the heading font. Static SVG. */
function Lanes({ ink, font, weight, mini }: { ink: string; font: string; weight?: string | number; mini?: boolean }) {
  const paths = useMemo(() => {
    const cx = 760;
    const cy = 330;
    const out: string[] = [];
    for (let i = 0; i < 9; i++) {
      const r = 150 + i * 34;
      out.push(`M ${cx - r} 470 L ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r} L 900 ${cy - r}`);
    }
    return out;
  }, []);
  return (
    <svg
      viewBox="0 0 800 450"
      preserveAspectRatio="xMaxYMax slice"
      // On the page the track keeps to the top 70% and at most 1000 px wide, so the start line and numbers sit above the fade.
      style={mini ? { ...fill, width: '100%', height: '100%', color: ink } : { position: 'absolute', top: 0, right: 0, width: 'min(100%, 1000px)', height: '70%', color: ink }}
    >
      <g fill="none" stroke="currentColor" strokeWidth={1.6} vectorEffect="non-scaling-stroke">
        {paths.map((d) => <path key={d} d={d} vectorEffect="non-scaling-stroke" />)}
        <path d={`M ${760 - 150 - 8 * 34} 430 L ${760 - 150} 430`} strokeWidth={3} vectorEffect="non-scaling-stroke" />
      </g>
      {Array.from({ length: 8 }, (_, j) => (
        <text key={j} x={760 - 150 - j * 34 - 17} y={420} textAnchor="middle" fontSize={20} fill="currentColor" style={{ fontFamily: font, fontWeight: weight ?? 700 }}>
          {j + 1}
        </text>
      ))}
    </svg>
  );
}

// ── Canvas effects ──────────────────────────────────────────────────────────

type Draw = (ctx: CanvasRenderingContext2D, w: number, h: number) => void;

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * A canvas that fills its box and is drawn once per size: a resize that
 * leaves the width alone (a phone's address bar showing and hiding) redraws
 * nothing. `key` changes (colour, seed) redraw.
 */
function DrawnCanvas({ draw, drawKey }: { draw: Draw; drawKey: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useIsoLayoutEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return;
    const measure = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      setSize((s) => (s && s.w === w && Math.abs(s.h - h) < 48 ? s : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const drawRef = useRef(draw);
  useIsoLayoutEffect(() => {
    drawRef.current = draw;
  });
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !size || !size.w || !size.h) return;
    // Sharp on a retina phone, but never a huge bitmap on a big monitor.
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    const budget = 3_000_000;
    if (size.w * size.h * dpr * dpr > budget) dpr = Math.sqrt(budget / (size.w * size.h));
    cv.width = Math.max(1, Math.round(size.w * dpr));
    cv.height = Math.max(1, Math.round(size.h * dpr));
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);
    drawRef.current(ctx, size.w, size.h);
  }, [size, drawKey]);

  return <canvas ref={ref} style={{ ...fill, width: '100%', height: size ? size.h : '100%', display: 'block' }} />;
}

function strokeSegments(ctx: CanvasRenderingContext2D, segs: Float32Array) {
  ctx.beginPath();
  for (let i = 0; i < segs.length; i += 4) {
    ctx.moveTo(segs[i], segs[i + 1]);
    ctx.lineTo(segs[i + 2], segs[i + 3]);
  }
  ctx.stroke();
}

/** Contour lines over the event's own terrain. */
function Topo({ ink, seed, mini }: { ink: string; seed: string; mini?: boolean }) {
  const draw: Draw = (ctx, w, h) => {
    const c = cachedTopoContours(seed, w, h, mini ? { step: 3, scale: 48 } : { step: w > 900 ? 7 : 6, scale: w > 900 ? 210 : 170 });
    ctx.strokeStyle = ink;
    ctx.lineJoin = 'round';
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = mini ? 0.7 : 0.9;
    strokeSegments(ctx, c.minor);
    ctx.globalAlpha = 1;
    ctx.lineWidth = mini ? 1 : 1.4;
    strokeSegments(ctx, c.major);
  };
  return <DrawnCanvas draw={draw} drawKey={`${ink}|${seed}|${mini ? 1 : 0}`} />;
}

/** Print dots, largest in the top-right corner. */
function Halftone({ ink, mini }: { ink: string; mini?: boolean }) {
  const draw: Draw = (ctx, w, h) => {
    const dots = halftoneDots(w, h, mini ? 5 : 10);
    ctx.fillStyle = ink;
    ctx.beginPath();
    for (let i = 0; i < dots.length; i += 3) {
      ctx.moveTo(dots[i] + dots[i + 2], dots[i + 1]);
      ctx.arc(dots[i], dots[i + 1], dots[i + 2], 0, Math.PI * 2);
    }
    ctx.fill();
  };
  return <DrawnCanvas draw={draw} drawKey={`${ink}|${mini ? 1 : 0}`} />;
}

/** The chalk-dust tile, one per colour, made once and reused by every layer and tile. */
const grainTiles = new Map<string, string>();
function grainTile(ink: string): string | null {
  if (typeof document === 'undefined') return null;
  let url = grainTiles.get(ink);
  if (!url) {
    const size = 180;
    const tc = document.createElement('canvas');
    tc.width = tc.height = size;
    const tx = tc.getContext('2d');
    if (!tx) return null;
    tx.fillStyle = ink;
    const specks = chalkGrain(size);
    for (let i = 0; i < specks.length; i += 4) {
      tx.globalAlpha = specks[i + 3];
      tx.fillRect(specks[i], specks[i + 1], specks[i + 2], specks[i + 2]);
    }
    url = tc.toDataURL('image/png');
    grainTiles.set(ink, url);
  }
  return url;
}

/** Chalk dust and a tally of reps. */
function Chalk({ ink, seed, mini }: { ink: string; seed: string; mini?: boolean }) {
  // Only ever drawn after mount (the layer is client-only), so the document is there.
  const tile = useMemo(() => grainTile(ink), [ink]);
  const draw: Draw = (ctx, w, h) => {
    const d = chalkDrawing(seed, w, h);
    for (const s of d.smudges) {
      const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r);
      g.addColorStop(0, ink);
      g.addColorStop(1, 'transparent');
      ctx.globalAlpha = s.alpha;
      ctx.fillStyle = g;
      ctx.fillRect(s.x - s.r, s.y - s.r, s.r * 2, s.r * 2);
    }
    ctx.strokeStyle = ink;
    ctx.lineCap = 'round';
    for (const l of d.lines) {
      ctx.globalAlpha = l.alpha;
      ctx.lineWidth = l.width;
      ctx.beginPath();
      ctx.moveTo(l.x1, l.y1);
      ctx.lineTo(l.x2, l.y2);
      ctx.stroke();
    }
  };
  return (
    <>
      {tile && <div style={{ ...fill, backgroundImage: `url("${tile}")`, backgroundSize: mini ? '90px 90px' : '180px 180px', opacity: 0.8 }} />}
      <DrawnCanvas draw={draw} drawKey={`${ink}|${seed}`} />
    </>
  );
}

// ── Sprint ──────────────────────────────────────────────────────────────────

/** Speed streaks crossing at an angle. Transform-only animation; still on reduced motion. */
function Sprint({ ink, tip, seed, count }: { ink: string; tip: string; seed: string; count: number }) {
  const streaks = useMemo(() => sprintStreaks(seed, count), [seed, count]);
  return (
    <>
      <style>{SPRINT_CSS}</style>
      <div style={{ position: 'absolute', top: '-30%', right: '-10%', bottom: '-30%', left: '-10%', transform: 'rotate(-9deg)' }}>
        {streaks.map((s, i) => (
          <i
            key={i}
            className="v2-fx-streak v2-fx-motion"
            style={{
              position: 'absolute',
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: `${s.width}%`,
              height: s.height,
              borderRadius: 2,
              opacity: s.opacity,
              background: `linear-gradient(90deg, transparent, ${ink} 75%, ${tip})`,
              ['--d' as string]: `${s.duration}s`,
              ['--dl' as string]: `${s.delay}s`,
            }}
          />
        ))}
      </div>
    </>
  );
}

// ── Outline type ────────────────────────────────────────────────────────────

let measureCtx: CanvasRenderingContext2D | null = null;

/** A huge outlined word in the heading font, sized to the layer once its font has loaded. */
function Outline({ ink, word, font, weight, mini }: { ink: string; word: string; font: string; weight?: string | number; mini?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<number | null>(null);

  useIsoLayoutEffect(() => {
    const el = ref.current;
    const box = el?.parentElement;
    if (!el || !box) return;
    let live = true;
    let lastW = -1;
    const fit = (force = false) => {
      const w = box.clientWidth;
      const h = box.clientHeight;
      if (!live || !w || (!force && w === lastW)) return;
      lastW = w;
      const cs = getComputedStyle(el);
      measureCtx = measureCtx ?? document.createElement('canvas').getContext('2d');
      let at100 = word.length * 62;
      if (measureCtx) {
        measureCtx.font = `${cs.fontWeight} 100px ${cs.fontFamily}`;
        at100 = measureCtx.measureText(word).width || at100;
      }
      const share = mini ? 0.92 : w < 640 ? 0.96 : 0.52;
      setSize(Math.min((w * share * 100) / at100, h * (mini ? 1.15 : 0.5)));
    };
    fit(true);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => fit());
    ro?.observe(box);
    const fonts = el.ownerDocument.fonts;
    fonts?.ready.then(() => fit(true)).catch(() => {});
    const onFonts = () => fit(true);
    fonts?.addEventListener?.('loadingdone', onFonts);
    return () => {
      live = false;
      ro?.disconnect();
      fonts?.removeEventListener?.('loadingdone', onFonts);
    };
  }, [word, font, weight, mini]);

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        right: mini ? '-6%' : '-2.5%',
        top: mini ? '-14%' : '1%',
        fontFamily: font,
        fontWeight: weight ?? 700,
        fontSize: size ?? 0,
        lineHeight: 0.82,
        // A heading font with no bold face would be thickened by the browser, which strokes every edge twice.
        fontSynthesis: 'none',
        whiteSpace: 'nowrap',
        color: 'transparent',
        WebkitTextStroke: `${mini ? 1 : 1.5}px ${ink}`,
        visibility: size ? 'visible' : 'hidden',
      }}
    >
      {word}
    </div>
  );
}
